import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { SortableImage } from './SortableImage';
import { useTranslation } from 'react-i18next';
import { ReportItem } from './PdfImages';
import IconBin from '../../Icons/IconBin/IconBin';
import IconOpenEye from '../../Icons/IconOpenEye/IconOpenEye';
import Modal from '../../Modal/Modal';
import Tooltip from '../../Tooltip';
import PdfPagesViewerModal from './PdfPagesViewerModal';
import { groupPdfPages, PdfPageGroup } from './groupPdfPages';

interface ReportsImagesProps {
  title: string;
  reports: ReportItem[];
  loading: boolean;
  uploadAndSplitPdf?: (params: { file: File; cycleId: string }) => Promise<void>;
  updateReportPositions: (reports: ReportItem[]) => Promise<void>;
  updateReportInclude: (params: { reportId: string; isIncluded: boolean }) => Promise<void>;
  deleteReport?: (params: { cycleId: string, reportId: string }) => Promise<void>;
  onDataChanged?: () => void;
}

const ReportsImages: React.FC<ReportsImagesProps> = ({
  title,
  loading,
  reports,
  uploadAndSplitPdf,
  updateReportPositions,
  updateReportInclude,
  deleteReport,
  onDataChanged,
}) => {
  const { id: cycleId } = useParams<{ id: string }>();
  const [orderedReports, setOrderedReports] = useState<ReportItem[]>([]);
  const [groupToDelete, setGroupToDelete] = useState<PdfPageGroup<ReportItem> | null>(null);
  const [viewerGroup, setViewerGroup] = useState<PdfPageGroup<ReportItem> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  // Only the uploaded-PDF grid (the one with an upload button) gets
  // collapsed to one card per document - the chart-screenshot grid has no
  // multi-page documents to group.
  const isPdfSection = !!uploadAndSplitPdf;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setOrderedReports(reports.sort((a, b) => a.position - b.position));
  }, [reports]);

  const pdfGroups = isPdfSection ? groupPdfPages(orderedReports) : orderedReports.map((report) => ({ key: report.image, pages: [report] }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = pdfGroups.findIndex((group) => group.key === active.id);
      const newIndex = pdfGroups.findIndex((group) => group.key === over.id);

      const newGroups = arrayMove(pdfGroups, oldIndex, newIndex);
      const newItems = newGroups.flatMap((group) => group.pages).map((item, index) => ({
        ...item,
        position: index,
      }));

      setOrderedReports(newItems);
      updateReportPositions(newItems);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    try {
      await uploadAndSplitPdf({ file, cycleId });
      onDataChanged?.();
    } catch (err) {
      console.error('Error uploading PDF', err);
    }
  };

  const handleIncludeChange = (reportId: string, isIncluded: boolean) => {
    updateReportInclude({ reportId, isIncluded });
  };

  const openDeletionModal = (group: PdfPageGroup<ReportItem>) => {
    setGroupToDelete(group);
  };

  const closeDeletionModal = () => {
    setGroupToDelete(null);
  };

  const deleteAttachment = async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    try {
      await Promise.all(groupToDelete.pages.map((page) => deleteReport({ reportId: page.id, cycleId })));
      onDataChanged?.();
    } finally {
      setIsDeleting(false);
      setGroupToDelete(null);
    }
  };

  const closeViewer = () => {
    setViewerGroup(null);
    onDataChanged?.();
  };

  return (
    <>
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-black dark:text-white truncate">{title}</h3>
            {uploadAndSplitPdf && (
              <>
                <label
                  htmlFor="pdfUpload"
                  className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50 text-nowrap"
                >
                  {loading ? t('buttons.uploading') : t('buttons.uploadPdf')}
                </label>
                <input id="pdfUpload" type="file" accept="application/pdf" onChange={handleFileUpload} className="hidden" />
              </>
            )}
          </div>
        </div>

        <div className="p-7">
          {loading ? (
            <div className="flex items-center justify-center p-7">
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
            </div>
          ) : pdfGroups.length === 0 ? (
            <div className="text-gray-500 text-center text-sm">{t('labels.noReportsFound')}</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={pdfGroups.map((group) => group.key)} strategy={rectSortingStrategy}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  {pdfGroups.map((group) => {
                    const firstPage = group.pages[0];
                    return (
                      <div key={group.key} className="relative">
                        {!isPdfSection && (
                          <div className="absolute top-2 right-2 z-50">
                            <input
                              type="checkbox"
                              defaultChecked={firstPage.is_included}
                              onChange={(e) => {
                                handleIncludeChange(firstPage.id, e.target.checked);
                              }}
                              className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                            />
                          </div>
                        )}
                        {deleteReport && typeof deleteReport === 'function' && (
                          <div className='absolute top-2 left-2 z-50'>
                            <IconBin
                              iconClassName='text-red border rounded bg-red px-1 py-0.5 bg-white cursor-pointer hover:text-white hover:bg-red hover:border-red active:text-white active:bg-primary active:border-primary'
                              onClick={() => openDeletionModal(group)}
                            />
                          </div>
                        )}
                        {group.pages.length > 1 && (
                          <div className="absolute bottom-2 left-2 z-50 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
                            {group.pages.length} pages
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 z-50">
                          <Tooltip hint={isPdfSection ? 'View full screen / select pages' : 'View full screen'}>
                            <IconOpenEye
                              iconClassName="rounded border border-foggy-mist bg-white px-1 py-1 text-black cursor-pointer hover:bg-primary hover:text-white"
                              onClick={() => setViewerGroup(group)}
                            />
                          </Tooltip>
                        </div>
                        <SortableImage
                          id={group.key}
                          report={firstPage}
                        />
                      </div>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div >
      <Modal
        modalTitle={t('report.modal.reportPage.delete.title')}
        isVisible={Boolean(groupToDelete)}
        content={
          groupToDelete && groupToDelete.pages.length > 1
            ? `Are you sure you want to delete this PDF (${groupToDelete.pages.length} pages)?`
            : t('report.modal.reportPage.delete.description')
        }
        acceptBtnText={isDeleting ? t('buttons.deleting', 'Deleting...') : t('buttons.delete')}
        acceptBtnDisabled={isDeleting}
        onClose={closeDeletionModal}
        onAccept={deleteAttachment}
        showAcceptBtn
        showCancelBtn
      />
      <PdfPagesViewerModal
        isVisible={!!viewerGroup}
        title={title}
        pages={(viewerGroup?.pages ?? []).map((report) => ({
          id: report.id,
          image: report.image,
          isIncluded: report.is_included,
        }))}
        initialPageId={viewerGroup?.pages[0]?.id ?? null}
        onClose={closeViewer}
        onToggleInclude={handleIncludeChange}
      />
    </>
  );
};

export default ReportsImages;
